import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import classNames from 'classnames';
import { cloneDeep } from 'lodash-es';
import environment from '@department-of-veterans-affairs/platform-utilities/environment';
import { getDefaultFormState } from '@department-of-veterans-affairs/react-jsonschema-form/lib/utils';
import {
  isReactComponent,
  focusByOrder,
  defaultFocusSelector,
} from 'platform/utilities/ui';
import { scrollToTop, customScrollAndFocus } from 'platform/utilities/scroll';

import navigationState from 'platform/forms-system/src/js/utilities/navigation/navigationState';
import get from '../../../../utilities/data/get';
import set from '../../../../utilities/data/set';

import FormNavButtons, {
  FormNavButtonContinue,
} from '../components/FormNavButtons';
import SchemaForm from '../components/SchemaForm';
import { setData, uploadFile } from '../actions';
import {
  getNextPagePath,
  getPreviousPagePath,
  checkValidPagePath,
  goBack,
} from '../routing';
import { DevModeNavLinks } from '../components/dev/DevModeNavLinks';
import {
  getPageKeys,
  isNavButtonAnalyticsEnabled,
  stringifyUrlParams,
} from '../helpers';
import {
  REVIEW_EDIT_DEPENDS_WARNING_BODY,
  REVIEW_EDIT_DEPENDS_WARNING_TITLE,
  collectReviewEditChangedChapters,
  finishReviewEditTrip,
  getNextReviewEditLocation,
  getReviewPagePath,
  getReviewReturnLocation,
  isReviewEditTrip,
  rememberReviewEditPageKey,
  rememberReviewEditPages,
  REVIEW_EDIT_EVENTS,
  recordReviewEditEvent,
  setReviewEditChangedChapters,
  setReviewEditReturnFocus,
  setReviewEditSuccessAlert,
  stashReviewEditBaseline,
  startReviewEditTrip,
  takeReviewEditBaseline,
} from '../review/reviewEditTrip';

const focusForm = async (route, index) => {
  const useCustomScrollAndFocus = route.formConfig?.useCustomScrollAndFocus;
  const scrollAndFocusTarget =
    route.pageConfig?.scrollAndFocusTarget ||
    route.formConfig?.scrollAndFocusTarget;
  if (useCustomScrollAndFocus === false) {
    focusByOrder(defaultFocusSelector);
  } else if (useCustomScrollAndFocus || scrollAndFocusTarget) {
    return customScrollAndFocus(scrollAndFocusTarget, index);
  }

  focusByOrder(defaultFocusSelector);
  return scrollToTop();
};

function getContentBeforeAndAfterButtons(
  route,
  contentBeforeButtons,
  contentAfterButtons,
  extraBeforeSaveLink = null,
) {
  const content = {
    contentBeforeNavButtons: contentBeforeButtons,
    contentAfterNavButtons: contentAfterButtons,
  };

  if (route.pageConfig?.hideSaveLinkAndStatus) {
    content.contentBeforeNavButtons = null;
    content.contentAfterNavButtons = null;
  } else if (route.formConfig?.showSaveLinkAfterButtons) {
    content.contentBeforeNavButtons = null;
    content.contentAfterNavButtons = (
      <>
        {contentBeforeButtons}
        {contentAfterButtons && (
          <div className="vads-u-margin-top--2">{contentAfterButtons}</div>
        )}
      </>
    );
  }

  if (extraBeforeSaveLink) {
    if (route.formConfig?.showSaveLinkAfterButtons) {
      content.contentAfterNavButtons = (
        <>
          {extraBeforeSaveLink}
          {content.contentAfterNavButtons}
        </>
      );
    } else {
      content.contentBeforeNavButtons = (
        <>
          {extraBeforeSaveLink}
          {content.contentBeforeNavButtons}
        </>
      );
    }
  }

  return content;
}

class FormPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      reviewEditDraft: isReviewEditTrip(
        props.route?.formConfig,
        props.featureToggles,
        props.location,
      )
        ? cloneDeep(props.form.data)
        : null,
    };
    if (
      isReviewEditTrip(
        props.route?.formConfig,
        props.featureToggles,
        props.location,
      )
    ) {
      this.ensureReviewEditTrip(props);
    }
  }

  componentDidMount() {
    this.prePopulateArrayData();
    if (!this.props.blockScrollOnMount) {
      focusForm(this.props.route, this.props?.params?.index);
    }
  }

  componentDidUpdate(prevProps) {
    const pageChanged =
      prevProps.route.pageConfig.pageKey !==
        this.props.route.pageConfig.pageKey ||
      get('params.index', prevProps) !== get('params.index', this.props);
    const nowReviewEdit = this.isReviewEdit();
    const wasReviewEdit = this.isReviewEdit(prevProps);

    if (pageChanged) {
      navigationState.resetNavigation();
      if (nowReviewEdit) {
        this.ensureReviewEditTrip();
        this.setState({ reviewEditDraft: cloneDeep(this.props.form.data) });
      } else if (this.state.reviewEditDraft) {
        this.setState({ reviewEditDraft: null });
      }
      this.prePopulateArrayData();
      focusForm(this.props.route, this.props?.params?.index);
    } else if (nowReviewEdit && !wasReviewEdit) {
      this.ensureReviewEditTrip();
      this.setState({ reviewEditDraft: cloneDeep(this.props.form.data) });
    }
  }

  isReviewEdit = (props = this.props) =>
    isReviewEditTrip(
      props.route?.formConfig,
      props.featureToggles,
      props.location,
    );

  reviewEditFormId = (props = this.props) =>
    props.route?.formConfig?.formId || props.form?.formId;

  reviewEditPageKey = (props = this.props) =>
    `${props.route?.pageConfig?.pageKey || ''}${props.params?.index ?? ''}`;

  reviewEditTrip = (props = this.props) => props.form?.reviewEditTrip || {};

  reviewEditTrackingPrefix = (props = this.props) =>
    props.form?.trackingPrefix || props.route?.formConfig?.trackingPrefix || '';

  recordReviewEdit = action => {
    recordReviewEditEvent(this.reviewEditTrackingPrefix(), action);
  };

  ensureReviewEditTrip = (props = this.props) => {
    const alreadyActive = Boolean(this.reviewEditTrip(props).active);
    const formId = this.reviewEditFormId(props);
    stashReviewEditBaseline(formId, props.form.data);
    props.startReviewEditTrip?.({
      formId,
      originPageKey: this.reviewEditPageKey(props),
      returnPath: getReviewPagePath(
        props.route.pageList,
        props.route.formConfig?.urlPrefix || '',
      ),
      pageKeys: getPageKeys(props.route.pageList, props.form.data),
    });
    if (!alreadyActive) {
      this.recordReviewEdit(REVIEW_EDIT_EVENTS.TRIP_START);
    }
  };

  completeReviewEdit = ({ success = false, formData } = {}) => {
    const { form, route } = this.props;
    const trip = this.reviewEditTrip();
    const pageKey = trip.originPageKey || this.reviewEditPageKey();
    setReviewEditReturnFocus(pageKey);
    let changedChapters = [];
    if (success) {
      this.recordReviewEdit(REVIEW_EDIT_EVENTS.UPDATE);
      setReviewEditSuccessAlert();
      this.props.rememberReviewEditPages?.(
        getPageKeys(route.pageList, formData || form.data),
      );
      changedChapters = collectReviewEditChangedChapters(
        route.pageList,
        formData || form.data,
        trip,
        form.pages,
      );
      if (changedChapters.length) {
        setReviewEditChangedChapters(changedChapters);
      }
    }
    takeReviewEditBaseline(this.reviewEditFormId());
    this.props.finishReviewEditTrip?.();
    this.props.router.push(
      getReviewReturnLocation(
        route.pageList,
        route.formConfig?.urlPrefix || '',
        pageKey,
        { success, changedChapters },
      ),
    );
  };

  cancelReviewEdit = () => {
    const baseline = takeReviewEditBaseline(this.reviewEditFormId());
    if (baseline) {
      this.props.setData(cloneDeep(baseline));
    }
    this.recordReviewEdit(REVIEW_EDIT_EVENTS.CANCEL);
    this.completeReviewEdit();
  };

  returnToReview = () => {
    this.completeReviewEdit();
  };

  onChange = formData => {
    const { pageConfig } = this.props.route;
    let newData = formData;
    if (pageConfig.showPagePerItem) {
      // If this is a per item page, the formData object will have data for a particular
      // row in an array, so we need to update the full form data object and then call setData
      newData = this.setArrayIndexedData(formData);
    }
    if (typeof pageConfig.updateFormData === 'function') {
      newData = pageConfig.updateFormData(
        this.formData(),
        newData,
        this.props.params.index,
      );
    }
    if (this.isReviewEdit()) {
      this.setState({ reviewEditDraft: newData });
      return;
    }
    this.props.setData(newData);
  };

  // Commit this page, then walk newly unlocked steps or return to review
  onSubmit = ({ formData }) => {
    const { form, route, location } = this.props;

    if (this.isReviewEdit()) {
      let newFormData = formData || this.state.reviewEditDraft || form.data;
      if (
        route.pageConfig.showPagePerItem &&
        (!route.pageConfig.CustomPage ||
          route.pageConfig.customPageUsesPagePerItemData)
      ) {
        newFormData = this.setArrayIndexedData(formData);
      }
      this.props.setData(newFormData);
      this.props.rememberReviewEditPageKey?.(this.reviewEditPageKey());
      const nextLocation = getNextReviewEditLocation(
        this.reviewEditTrip(),
        route.pageList,
        newFormData,
        location.pathname,
        this.reviewEditPageKey(),
      );
      if (nextLocation) {
        this.recordReviewEdit(REVIEW_EDIT_EVENTS.DEPENDS_WALK);
        this.props.router.push(nextLocation);
        return;
      }
      this.completeReviewEdit({ success: true, formData: newFormData });
      return;
    }

    let newFormData = formData || form.data;

    // This makes sure defaulted data on a page with no changes is saved
    // Probably safe to do this for regular pages, too, but it hasn’t been
    // necessary. Additionally, it should NOT setData for a CustomPage. The
    // CustomPage should take care of that itself.
    if (
      route.pageConfig.showPagePerItem &&
      (!route.pageConfig.CustomPage ||
        route.pageConfig.customPageUsesPagePerItemData)
    ) {
      newFormData = this.setArrayIndexedData(formData);
      this.props.setData(newFormData);
    }

    const path = getNextPagePath(
      route.pageList,
      newFormData,
      location.pathname,
    );

    if (typeof route.pageConfig.onNavForward === 'function') {
      route.pageConfig.onNavForward({
        formData: newFormData,
        goPath: customPath => this.props.router.push(customPath),
        goNextPath: urlParams => {
          const urlParamsString = stringifyUrlParams(urlParams);
          this.props.router.push(path + (urlParamsString || ''));
        },
        pageList: route.pageList,
        pathname: location.pathname,
        setFormData: this.props.setData,
        urlParams: location.query,
        urlPrefix: route.formConfig?.urlPrefix || '',
        index: this.props.params?.index,
      });
      return;
    }

    this.props.router.push(path);
  };

  getArrayIndexedData = () => {
    const { route, params, form } = this.props;
    const data = this.isReviewEdit()
      ? this.state.reviewEditDraft || form.data
      : form.data;
    return get([route.pageConfig.arrayPath, params.index], data);
  };

  // returns a duplicate of formData with newData at the indexed array
  setArrayIndexedData = newData => {
    let formData = this.isReviewEdit()
      ? this.state.reviewEditDraft || this.props.form.data
      : this.props.form.data;
    const { arrayPath } = this.props.route.pageConfig;
    if (!get(arrayPath, formData)) {
      // if array doesn't exist create it
      formData = set([arrayPath], [], formData);
    }
    return set([arrayPath, this.props.params.index], newData, formData);
  };

  prePopulateArrayData = () => {
    const { pageConfig, formConfig } = this.props.route;
    // only applicable to array routes with these settings
    if (pageConfig.showPagePerItem && pageConfig.allowPathWithNoItems) {
      const arrayFormData = this.getArrayIndexedData();
      if (!arrayFormData) {
        // we are trying to visit a route where there is no formData
        // for this index in the array, so we need to create and
        // pre-populate it with empty values for SchemaForm to work properly
        const defaultData = getDefaultFormState(
          pageConfig.schema.properties[pageConfig.arrayPath].items ||
            pageConfig.schema.properties[pageConfig.arrayPath].additionalItems,
          undefined,
          // pageConfig.schema is the unresolved route config, so $refs need definitions here
          formConfig?.defaultDefinitions || {},
        );
        const newData = this.setArrayIndexedData(defaultData);
        if (this.isReviewEdit()) {
          this.setState({ reviewEditDraft: newData });
          return;
        }
        this.props.setData(newData);
      }
    }
  };

  /**
   * @param {Object} [options]
   * @param {boolean} [options.all] If true, return the entire form data regardless of context
   */
  formData = ({ all } = {}) => {
    const sourceData = this.isReviewEdit()
      ? this.state.reviewEditDraft || this.props.form.data
      : this.props.form.data;

    if (all) {
      return sourceData;
    }

    const { pageConfig } = this.props.route;
    // If it's a CustomPage, return the entire form data
    if (pageConfig.CustomPage && !pageConfig.customPageUsesPagePerItemData) {
      return sourceData;
    }

    // If it's an array page, return only the data for that array item
    // Otherwise, return the data for the entire form
    return this.props.route.pageConfig.showPagePerItem
      ? this.getArrayIndexedData()
      : sourceData;
  };

  goBack = () => {
    if (this.isReviewEdit()) {
      this.cancelReviewEdit();
      return;
    }
    goBack({
      formData: this.props.form.data,
      index: this.props.params?.index,
      location: this.props.location,
      onNavBack: this.props.route.pageConfig?.onNavBack,
      router: this.props.router,
      setData: this.props.setData,
      pageList: this.props.route?.pageList,
      urlPrefix: this.props.route?.formConfig?.urlPrefix || '',
    });
  };

  goToPath = (customPath, options = {}) => {
    const {
      form,
      route: { pageList },
      location,
    } = this.props;
    const { force, chapterKey } = options;

    const path =
      customPath &&
      (force || checkValidPagePath(pageList, this.props.form.data, customPath))
        ? customPath
        : getPreviousPagePath(pageList, form.data, location.pathname);

    const { pathname, search } = new URL(path, window.location.origin);
    this.props.router.push({
      pathname,
      search,
      state: { chapterKey },
    });
  };

  onContinue = () => {
    const { route } = this.props;
    if (typeof route.pageConfig.onContinue === 'function') {
      // pass in data & set form data function to allow modifying data or
      // flags upon leaving a page
      route.pageConfig.onContinue(this.formData(), this.props.setData);
    }
  };

  render() {
    const {
      route,
      params,
      form,
      contentBeforeButtons,
      contentAfterButtons,
      formContext,
      appStateData,
    } = this.props;

    const pageProps = form.pages[route.pageConfig.pageKey];
    let { schema, uiSchema } = pageProps;

    const pageClasses = classNames('form-panel', route.pageConfig.pageClass);
    const data = this.formData();

    const formOptions = route.formConfig?.formOptions || {};

    if (
      route.pageConfig.showPagePerItem &&
      (!route.pageConfig.CustomPage ||
        route.pageConfig.customPageUsesPagePerItemData)
    ) {
      // Instead of passing through the schema/uiSchema to SchemaForm, the
      // current item schema for the array at arrayPath is pulled out of the page state and passed
      const { items, additionalItems } =
        schema.properties[route.pageConfig.arrayPath];
      schema = items[params.index];
      if (!schema && route.pageConfig.allowPathWithNoItems) {
        schema = additionalItems;
      }
      // Similarly, the items uiSchema and the data for just that particular item are passed
      uiSchema = uiSchema[route.pageConfig.arrayPath].items;
    }
    // It should be "safe" to check that this is the first page because it is
    // always eligible and enabled, no need to call getPreviousPagePath.
    const isFirstRoutePage =
      route.pageList[0].path === this.props.location.pathname;

    const reviewEdit = this.isReviewEdit();
    const dependsWarning =
      reviewEdit && route.pageConfig?.reviewDependsWarning ? (
        <va-alert-expandable
          class="vads-u-margin-bottom--2"
          status="info"
          trigger={REVIEW_EDIT_DEPENDS_WARNING_TITLE}
        >
          <p>{REVIEW_EDIT_DEPENDS_WARNING_BODY}</p>
        </va-alert-expandable>
      ) : null;
    const showNavLinks =
      environment.isLocalhost() && route.formConfig?.dev?.showNavLinks;
    const hideNavButtons =
      !environment.isProduction() &&
      (formOptions?.noBottomNav || route.pageConfig?.hideNavButtons);

    let pageContentBeforeButtons = route.pageConfig?.ContentBeforeButtons;
    if (
      route.pageConfig?.ContentBeforeButtons &&
      isReactComponent(route.pageConfig.ContentBeforeButtons)
    ) {
      pageContentBeforeButtons = (
        <route.pageConfig.ContentBeforeButtons
          formData={data}
          formContext={formContext}
          router={this.props.router}
          setFormData={this.props.setData}
        />
      );
    }
    const DefaultNavButtons =
      reviewEdit || !route.formConfig?.useTopBackLink
        ? FormNavButtons
        : FormNavButtonContinue;
    const NavButtonsWithWrapper = formOptions?.NavButtonsWithWrapper;
    const isValidNavWrapper = isReactComponent(NavButtonsWithWrapper);
    if (
      !environment.isProduction() &&
      NavButtonsWithWrapper &&
      !isValidNavWrapper
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        '[FormPage] NavButtonsWithWrapper is set but is not a valid component. Falling back to default buttons.',
      );
    }
    const NavButtons = isValidNavWrapper
      ? NavButtonsWithWrapper
      : DefaultNavButtons;

    const { contentBeforeNavButtons, contentAfterNavButtons } =
      getContentBeforeAndAfterButtons(
        route,
        contentBeforeButtons,
        contentAfterButtons,
        dependsWarning,
      );

    const reviewEditNavProps = reviewEdit
      ? {
          primaryButtonText: 'Update',
          secondaryButtonText: 'Cancel',
          beforeText: '',
          afterText: '',
          enableAnalytics: true,
        }
      : {};
    const navButtonProps = {
      goBack: (reviewEdit || !isFirstRoutePage) && this.goBack,
      goForward: this.onContinue,
      submitToContinue: true,
      useWebComponents: formOptions.useWebComponentForNavigation,
      enableAnalytics: isNavButtonAnalyticsEnabled(formOptions),
      ...reviewEditNavProps,
    };
    const CustomFormPageTopContent = route.formConfig?.CustomFormPageTopContent;

    // Bypass the SchemaForm and render the custom component
    // NOTE: I don't think FormPage is rendered on the review page, so I believe
    // onReviewPage will always be false here
    if (isReactComponent(route.pageConfig.CustomPage)) {
      return (
        <div className={pageClasses}>
          {showNavLinks && (
            <DevModeNavLinks
              pageList={route.pageList}
              collapsible={route.formConfig?.dev?.collapsibleNavLinks}
            />
          )}
          {CustomFormPageTopContent && (
            <CustomFormPageTopContent
              currentLocation={this.props.location}
              currentPageKey={route.pageConfig.pageKey}
              fullData={form.data}
              pagePerItemIndex={params ? params.index : undefined}
            />
          )}
          <route.pageConfig.CustomPage
            name={route.pageConfig.pageKey}
            title={route.pageConfig.title}
            data={data}
            fullData={form.data}
            pagePerItemIndex={params ? params.index : undefined}
            onReviewPage={formContext?.onReviewPage}
            trackingPrefix={this.props.form.trackingPrefix}
            uploadFile={this.props.uploadFile}
            schema={schema}
            uiSchema={uiSchema}
            getFormData={this.formData}
            goBack={this.goBack}
            goForward={this.onSubmit}
            goToPath={this.goToPath}
            onContinue={this.onContinue}
            onChange={this.onChange}
            onSubmit={this.onSubmit}
            setFormData={this.props.setData}
            pageContentBeforeButtons={pageContentBeforeButtons}
            contentBeforeButtons={contentBeforeNavButtons}
            contentAfterButtons={contentAfterNavButtons}
            appStateData={appStateData}
            formContext={this.formContext}
            NavButtons={NavButtons}
            DefaultNavButtons={DefaultNavButtons}
            formOptions={formOptions}
            urlPrefix={route.formConfig?.urlPrefix || ''}
          />
        </div>
      );
    }

    return (
      <div className={pageClasses}>
        {showNavLinks && (
          <DevModeNavLinks
            pageList={route.pageList}
            collapsible={route.formConfig?.dev?.collapsibleNavLinks}
          />
        )}
        {CustomFormPageTopContent && (
          <CustomFormPageTopContent
            currentLocation={this.props.location}
            currentPageKey={route.pageConfig.pageKey}
            fullData={form.data}
            pagePerItemIndex={params ? params.index : undefined}
          />
        )}
        <SchemaForm
          name={route.pageConfig.pageKey}
          title={route.pageConfig.title}
          data={data}
          appStateData={appStateData}
          schema={schema}
          uiSchema={uiSchema}
          pagePerItemIndex={params ? params.index : undefined}
          formContext={formContext}
          getFormData={this.formData}
          trackingPrefix={this.props.form.trackingPrefix}
          uploadFile={this.props.uploadFile}
          onChange={this.onChange}
          onSubmit={this.onSubmit}
          formOptions={formOptions}
        >
          {pageContentBeforeButtons}
          {hideNavButtons ? (
            <div />
          ) : (
            <>
              {contentBeforeNavButtons}
              {/* Allow apps to wrap the default nav buttons (e.g., tracking) without changing UI */}
              {isValidNavWrapper ? (
                <NavButtonsWithWrapper
                  DefaultNavButtons={DefaultNavButtons}
                  {...navButtonProps}
                />
              ) : (
                <DefaultNavButtons {...navButtonProps} />
              )}
              {contentAfterNavButtons}
            </>
          )}
        </SchemaForm>
      </div>
    );
  }
}

function mapStateToProps(state, ownProps) {
  const { appStateSelector } = ownProps.route.pageConfig;
  return {
    form: state.form,
    user: state.user,
    appStateData: appStateSelector && appStateSelector(state),
    featureToggles: state.featureToggles || {},
  };
}

const mapDispatchToProps = {
  setData,
  uploadFile,
  startReviewEditTrip,
  finishReviewEditTrip,
  rememberReviewEditPages,
  rememberReviewEditPageKey,
};

FormPage.propTypes = {
  form: PropTypes.object.isRequired,
  appStateData: PropTypes.shape({}),
  blockScrollOnMount: PropTypes.bool,
  contentAfterButtons: PropTypes.element,
  contentBeforeButtons: PropTypes.element,
  featureToggles: PropTypes.object,
  finishReviewEditTrip: PropTypes.func,
  formContext: PropTypes.shape({
    onReviewPage: PropTypes.bool,
  }),
  location: PropTypes.shape({
    pathname: PropTypes.string,
    query: PropTypes.object,
  }),
  params: PropTypes.shape({
    // for testing only?
    index: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  rememberReviewEditPageKey: PropTypes.func,
  rememberReviewEditPages: PropTypes.func,
  route: PropTypes.shape({
    pageConfig: PropTypes.shape({
      allowPathWithNoItems: PropTypes.bool,
      arrayPath: PropTypes.string,
      ContentBeforeButtons: PropTypes.oneOfType([
        PropTypes.element,
        PropTypes.elementType,
        PropTypes.func,
      ]),
      CustomPage: PropTypes.oneOfType([
        PropTypes.element,
        PropTypes.elementType,
        PropTypes.func,
      ]),
      customPageUsesPagePerItemData: PropTypes.bool,
      hideNavButtons: PropTypes.bool,
      hideSaveLinkAndStatus: PropTypes.bool,
      onContinue: PropTypes.func,
      onNavBack: PropTypes.func,
      onNavForward: PropTypes.func,
      pageClass: PropTypes.string,
      pageKey: PropTypes.string.isRequired,
      schema: PropTypes.object.isRequired,
      scrollAndFocusTarget: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.func,
      ]),
      showPagePerItem: PropTypes.bool,
      title: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
      uiSchema: PropTypes.object.isRequired,
      updateFormData: PropTypes.func,
    }),
    formConfig: PropTypes.shape({
      CustomFormPageTopContent: PropTypes.elementType,
      defaultDefinitions: PropTypes.object,
      dev: PropTypes.shape({
        showNavLinks: PropTypes.bool,
        collapsibleNavLinks: PropTypes.bool,
      }),
      formOptions: PropTypes.shape({
        NavButtonsWithWrapper: PropTypes.elementType,
        analytics: PropTypes.shape({ navButtons: PropTypes.bool }),
        noBottomNav: PropTypes.bool,
        useWebComponentForNavigation: PropTypes.bool,
      }),
      showSaveLinkAfterButtons: PropTypes.bool,
      urlPrefix: PropTypes.string,
      useTopBackLink: PropTypes.bool,
    }),
    pageList: PropTypes.arrayOf(
      PropTypes.shape({
        path: PropTypes.string.isRequired,
      }),
    ),
  }),
  router: PropTypes.shape({
    push: PropTypes.func,
  }),
  setData: PropTypes.func,
  startReviewEditTrip: PropTypes.func,
  uploadFile: PropTypes.func,
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(FormPage),
);

export { FormPage };
